import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import ConfirmDialog from '@/components/ConfirmDialog';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import '@/pages/Products.css';
import '@/pages/Clients.css';

const emptyVariable = (type = 'number') => ({
  key: '',
  label: '',
  type,
  unit: '',
  default_value: 0,
  options: type === 'select' ? [{ label: '', value: 0 }] : [],
  checked_value: 0,
  unchecked_value: 0,
  formula: '',
  show_in_budget: type !== 'calculated',
});

const normalizeVariable = (v) => ({
  ...emptyVariable(v.type),
  ...v,
  options: v.options || [],
});

const Settings = () => {
  const location = useLocation();
  const { operators, activeOperator, setActiveOperator, loadOperators } = useAuth();
  const [variables, setVariables] = useState([]);
  const [operatorName, setOperatorName] = useState('');
  const [operatorEdits, setOperatorEdits] = useState({});
  const [savingOperator, setSavingOperator] = useState(false);
  const [deleteOperator, setDeleteOperator] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [workerForm, setWorkerForm] = useState({ name: '', email: '', password: '' });
  const [workerEdits, setWorkerEdits] = useState({});
  const [savingWorker, setSavingWorker] = useState(false);
  const [deleteWorker, setDeleteWorker] = useState(null);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [savingPassword, setSavingPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingVariables, setSavingVariables] = useState(false);
  const [error, setError] = useState('');
  const [savedMessage, setSavedMessage] = useState('');
  const [openSections, setOpenSections] = useState({
    password: false,
    operadores: false,
    trabajadores: false,
    variables: false,
  });

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const [variablesRes, workersRes] = await Promise.all([
        api.get('/settings/global-variables'),
        api.get('/workers'),
      ]);
      const loadedVariables = (variablesRes.data.variables || []).map(normalizeVariable);
      setVariables(loadedVariables);
      setWorkers(workersRes.data || []);
    } catch (err) {
      setError('Error al cargar configuracion');
    } finally {
      setLoading(false);
    }
  };

  const addVariable = (type) => {
    setVariables([...variables, emptyVariable(type)]);
    setSavedMessage('');
  };

  const updateVariable = (index, patch) => {
    const copy = [...variables];
    copy[index] = { ...copy[index], ...patch };
    setVariables(copy);
    setSavedMessage('');
  };

  const removeVariable = (index) => {
    const copy = [...variables];
    copy.splice(index, 1);
    setVariables(copy);
    setSavedMessage('');
  };

  const addOption = (varIndex) => {
    const copy = [...variables];
    copy[varIndex].options = [...(copy[varIndex].options || []), { label: '', value: 0 }];
    setVariables(copy);
    setSavedMessage('');
  };

  const updateOption = (varIndex, optIndex, patch) => {
    const copy = [...variables];
    const opts = [...copy[varIndex].options];
    opts[optIndex] = { ...opts[optIndex], ...patch };
    copy[varIndex].options = opts;
    setVariables(copy);
    setSavedMessage('');
  };

  const removeOption = (varIndex, optIndex) => {
    const copy = [...variables];
    const opts = [...copy[varIndex].options];
    opts.splice(optIndex, 1);
    copy[varIndex].options = opts;
    setVariables(copy);
    setSavedMessage('');
  };

  const serializeVariables = () => variables.map(v => {
    const base = {
      key: v.key.trim(),
      label: v.label.trim(),
      type: v.type,
      unit: v.unit || '',
      show_in_budget: v.type === 'calculated' ? false : v.show_in_budget !== false,
    };
    if (v.type === 'number') {
      return { ...base, default_value: Number(v.default_value) || 0 };
    }
    if (v.type === 'select') {
      return {
        ...base,
        options: (v.options || []).map(o => ({ label: o.label, value: Number(o.value) || 0 })),
      };
    }
    if (v.type === 'checkbox') {
      return {
        ...base,
        checked_value: Number(v.checked_value) || 0,
        unchecked_value: Number(v.unchecked_value) || 0,
      };
    }
    if (v.type === 'calculated') {
      return {
        ...base,
        formula: v.formula || '',
      };
    }
    return base;
  });

  const handleSaveVariables = async () => {
    setError('');
    setSavedMessage('');
    setSavingVariables(true);
    try {
      const { data } = await api.put('/settings/global-variables', {
        variables: serializeVariables(),
      });
      const savedVariables = (data.variables || []).map(normalizeVariable);
      setVariables(savedVariables);
      setSavedMessage('Variables globales guardadas');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Error al guardar variables globales');
    } finally {
      setSavingVariables(false);
    }
  };

  const handleCreateOperator = async () => {
    const name = operatorName.trim();
    if (!name) {
      setError('El nombre del operador es obligatorio');
      return;
    }
    setError('');
    setSavedMessage('');
    setSavingOperator(true);
    try {
      const { data } = await api.post('/operators', { name });
      setOperatorName('');
      await loadOperators();
      if (operators.length === 0) {
        setActiveOperator(data);
      }
      setSavedMessage('Operador guardado');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Error al guardar operador');
    } finally {
      setSavingOperator(false);
    }
  };

  const handleUpdateOperator = async (operator) => {
    const name = (operatorEdits[operator.id] ?? operator.name).trim();
    if (!name) {
      setError('El nombre del operador no puede estar vacio');
      return;
    }
    setError('');
    setSavedMessage('');
    setSavingOperator(true);
    try {
      const { data } = await api.put(`/operators/${operator.id}`, { name });
      await loadOperators();
      if (activeOperator?.id === operator.id) {
        setActiveOperator(data);
      }
      setSavedMessage('Operador actualizado');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Error al actualizar operador');
    } finally {
      setSavingOperator(false);
    }
  };

  const handleDeleteOperator = async () => {
    if (!deleteOperator) return;
    setError('');
    setSavedMessage('');
    setSavingOperator(true);
    try {
      await api.delete(`/operators/${deleteOperator.id}`);
      if (activeOperator?.id === deleteOperator.id) {
        setActiveOperator(null);
      }
      await loadOperators();
      setDeleteOperator(null);
      setSavedMessage('Operador eliminado');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Error al eliminar operador');
    } finally {
      setSavingOperator(false);
    }
  };

  const loadWorkers = async () => {
    const { data } = await api.get('/workers');
    setWorkers(data || []);
  };

  const handleCreateWorker = async () => {
    if (!workerForm.name.trim() || !workerForm.email.trim() || !workerForm.password.trim()) {
      setError('Nombre, email y contrasena del trabajador son obligatorios');
      return;
    }
    setError('');
    setSavedMessage('');
    setSavingWorker(true);
    try {
      await api.post('/workers', {
        name: workerForm.name.trim(),
        email: workerForm.email.trim(),
        password: workerForm.password,
      });
      setWorkerForm({ name: '', email: '', password: '' });
      await loadWorkers();
      setSavedMessage('Trabajador guardado');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Error al guardar trabajador');
    } finally {
      setSavingWorker(false);
    }
  };

  const handleUpdateWorker = async (worker) => {
    const patch = workerEdits[worker.id] || {};
    const payload = {
      name: (patch.name ?? worker.name).trim(),
      email: (patch.email ?? worker.email).trim(),
    };
    if (patch.password) payload.password = patch.password;
    setError('');
    setSavedMessage('');
    setSavingWorker(true);
    try {
      await api.put(`/workers/${worker.id}`, payload);
      await loadWorkers();
      setWorkerEdits(current => ({ ...current, [worker.id]: {} }));
      setSavedMessage('Trabajador actualizado');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Error al actualizar trabajador');
    } finally {
      setSavingWorker(false);
    }
  };

  const handleDeleteWorker = async () => {
    if (!deleteWorker) return;
    setError('');
    setSavedMessage('');
    setSavingWorker(true);
    try {
      await api.delete(`/workers/${deleteWorker.id}`);
      await loadWorkers();
      setDeleteWorker(null);
      setSavedMessage('Trabajador eliminado');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Error al eliminar trabajador');
    } finally {
      setSavingWorker(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      setError('Completa todos los campos de contrasena');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setError('La nueva contrasena y la confirmacion no coinciden');
      return;
    }

    setError('');
    setSavedMessage('');
    setSavingPassword(true);
    try {
      await api.put('/auth/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setSavedMessage('Contrasena actualizada');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Error al cambiar contrasena');
    } finally {
      setSavingPassword(false);
    }
  };

  const toggleSection = (section) => {
    setOpenSections(current => ({
      ...current,
      [section]: !current[section],
    }));
  };

  return (
    <div className="dashboard-container">
      <Sidebar activePath={location.pathname} />

      <main className="main-content">
        <div className="page-header-row">
          <div>
            <h1 data-testid="settings-title">Configuracion</h1>
            <p className="dashboard-subtitle">
              Variables globales para usar como referencia en el sistema.
            </p>
          </div>
        </div>

        {error && <div className="login-error">{error}</div>}
        {savedMessage && <div className="test-result" style={{ marginBottom: '1rem' }}>{savedMessage}</div>}

        <div className="card editor-card">
          <button
            type="button"
            className="settings-section-header"
            onClick={() => toggleSection('password')}
          >
            <span>
              <span className="settings-section-title">Contrasena</span>
              <span className="settings-section-subtitle">
                Actualiza la clave de acceso de la cuenta actual.
              </span>
            </span>
            <span className="settings-section-toggle">
              {openSections.password ? 'Guardar' : 'Desplegar'}
            </span>
          </button>

          {openSections.password ? (
            <div className="settings-section-body">
              <div className="variable-row">
                <div className="form-group">
                  <label className="form-label">Contrasena actual</label>
                  <input
                    type="password"
                    className="form-input"
                    autoComplete="current-password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    data-testid="current-password-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Nueva contrasena</label>
                  <input
                    type="password"
                    className="form-input"
                    autoComplete="new-password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    data-testid="new-password-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Confirmar nueva contrasena</label>
                  <input
                    type="password"
                    className="form-input"
                    autoComplete="new-password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              <button
                type="button"
                className="btn-primary-inline"
                onClick={handleChangePassword}
                disabled={savingPassword}
                data-testid="change-password-button"
              >
                {savingPassword ? 'Guardando...' : 'Cambiar Contrasena'}
              </button>
            </div>
          ) : (
            <div className="settings-section-summary">La contrasena queda protegida con hash bcrypt.</div>
          )}
        </div>

        <div className="card editor-card">
          <button
            type="button"
            className="settings-section-header"
            onClick={() => toggleSection('operadores')}
          >
            <span>
              <span className="settings-section-title">Operadores</span>
              <span className="settings-section-subtitle">
                Identifican quien realiza cada accion. No tienen usuario ni permisos propios.
              </span>
            </span>
            <span className="settings-section-toggle">
              {openSections.operadores ? 'Guardar' : 'Desplegar'}
            </span>
          </button>

          {openSections.operadores ? (
            <div className="settings-section-body">

          <div className="variable-row">
            <div className="form-group">
              <label className="form-label">Nuevo operador</label>
              <input
                type="text"
                className="form-input"
                value={operatorName}
                onChange={(e) => setOperatorName(e.target.value)}
                placeholder="Fabio"
                data-testid="operator-name-input"
              />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                className="btn-primary-inline"
                onClick={handleCreateOperator}
                disabled={savingOperator}
                data-testid="create-operator-button"
              >
                Agregar Operador
              </button>
            </div>
          </div>

          {operators.length === 0 ? (
            <div className="empty-state">
              Todavia no hay operadores. Si no cargas ninguno, las acciones se registran con la cuenta.
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Operador</th>
                  <th>Activo</th>
                  <th style={{ width: '220px', textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {operators.map(operator => (
                  <tr key={operator.id}>
                    <td>
                      <input
                        className="form-input"
                        value={operatorEdits[operator.id] ?? operator.name}
                        onChange={(e) => setOperatorEdits({ ...operatorEdits, [operator.id]: e.target.value })}
                        data-testid={`operator-edit-${operator.id}`}
                      />
                    </td>
                    <td className="cell-strong">
                      {activeOperator?.id === operator.id ? 'Si' : '-'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn-link" onClick={() => setActiveOperator(operator)}>
                        Usar
                      </button>
                      <button className="btn-link" onClick={() => handleUpdateOperator(operator)} disabled={savingOperator}>
                        Guardar
                      </button>
                      <button className="btn-link btn-link-danger" onClick={() => setDeleteOperator(operator)} disabled={savingOperator}>
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
            </div>
          ) : (
            <div className="settings-section-summary">
              {operators.length === 1 ? '1 operador cargado' : `${operators.length} operadores cargados`}
            </div>
          )}
        </div>

        <div className="card editor-card">
          <button
            type="button"
            className="settings-section-header"
            onClick={() => toggleSection('trabajadores')}
          >
            <span>
              <span className="settings-section-title">Trabajadores</span>
              <span className="settings-section-subtitle">
                Cuentas con acceso exclusivo al modulo de produccion.
              </span>
            </span>
            <span className="settings-section-toggle">
              {openSections.trabajadores ? 'Guardar' : 'Desplegar'}
            </span>
          </button>

          {openSections.trabajadores ? (
            <div className="settings-section-body">

          <div className="variable-row">
            <div className="form-group">
              <label className="form-label">Nombre</label>
              <input
                className="form-input"
                value={workerForm.name}
                onChange={(e) => setWorkerForm({ ...workerForm, name: e.target.value })}
                placeholder="Jose"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={workerForm.email}
                onChange={(e) => setWorkerForm({ ...workerForm, email: e.target.value })}
                placeholder="jose@imprenta.com"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Contrasena</label>
              <input
                type="password"
                className="form-input"
                value={workerForm.password}
                onChange={(e) => setWorkerForm({ ...workerForm, password: e.target.value })}
                placeholder="****"
              />
            </div>
          </div>
          <button className="btn-primary-inline" onClick={handleCreateWorker} disabled={savingWorker}>
            Agregar Trabajador
          </button>

          {workers.length === 0 ? (
            <div className="empty-state">Todavia no hay trabajadores cargados.</div>
          ) : (
            <table className="data-table" style={{ marginTop: '1rem' }}>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Nueva contrasena</th>
                  <th style={{ width: '180px', textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {workers.map(worker => {
                  const edit = workerEdits[worker.id] || {};
                  return (
                    <tr key={worker.id}>
                      <td>
                        <input
                          className="form-input"
                          value={edit.name ?? worker.name}
                          onChange={(e) => setWorkerEdits({ ...workerEdits, [worker.id]: { ...edit, name: e.target.value } })}
                        />
                      </td>
                      <td>
                        <input
                          className="form-input"
                          value={edit.email ?? worker.email}
                          onChange={(e) => setWorkerEdits({ ...workerEdits, [worker.id]: { ...edit, email: e.target.value } })}
                        />
                      </td>
                      <td>
                        <input
                          type="password"
                          className="form-input"
                          value={edit.password ?? ''}
                          onChange={(e) => setWorkerEdits({ ...workerEdits, [worker.id]: { ...edit, password: e.target.value } })}
                          placeholder="Dejar vacio"
                        />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn-link" onClick={() => handleUpdateWorker(worker)} disabled={savingWorker}>
                          Guardar
                        </button>
                        <button className="btn-link btn-link-danger" onClick={() => setDeleteWorker(worker)} disabled={savingWorker}>
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
            </div>
          ) : (
            <div className="settings-section-summary">
              {workers.length === 1 ? '1 trabajador cargado' : `${workers.length} trabajadores cargados`}
            </div>
          )}
        </div>

        <div className="card editor-card">
          <button
            type="button"
            className="settings-section-header"
            onClick={() => toggleSection('variables')}
          >
            <span>
              <span className="settings-section-title">Variables Globales</span>
              <span className="settings-section-subtitle">
                Valores reutilizables en productos y presupuestos.
              </span>
            </span>
            <span className="settings-section-toggle">
              {openSections.variables ? 'Guardar' : 'Desplegar'}
            </span>
          </button>

          {openSections.variables ? (
            <div className="settings-section-body">
            <div className="add-variable-buttons">
              <button className="btn-small" onClick={() => addVariable('number')}>+ Numero</button>
              <button className="btn-small" onClick={() => addVariable('select')}>+ Seleccion</button>
              <button className="btn-small" onClick={() => addVariable('checkbox')}>+ Casilla</button>
              <button className="btn-small" onClick={() => addVariable('calculated')}>+ Calculada</button>
            </div>

          {loading ? (
            <div className="empty-state">Cargando variables globales...</div>
          ) : variables.length === 0 ? (
            <div className="empty-state">
              Todavia no hay variables globales. Agrega una para usarla en cualquier formula.
            </div>
          ) : (
            variables.map((v, idx) => (
              <div key={idx} className="variable-card">
                <div className="variable-header">
                  <span className="variable-type-badge">{v.type}</span>
                  <div className="variable-header-actions">
                    <div className="visibility-toggle" aria-label="Visibilidad en presupuesto">
                      <button
                        type="button"
                        className={`visibility-toggle-btn ${v.type !== 'calculated' && v.show_in_budget !== false ? 'active' : ''}`}
                        disabled={v.type === 'calculated'}
                        onClick={() => updateVariable(idx, { show_in_budget: true })}
                      >
                        Visible
                      </button>
                      <button
                        type="button"
                        className={`visibility-toggle-btn ${v.type === 'calculated' || v.show_in_budget === false ? 'active' : ''}`}
                        disabled={v.type === 'calculated'}
                        onClick={() => updateVariable(idx, { show_in_budget: false })}
                      >
                        Oculta
                      </button>
                    </div>
                    <button className="btn-remove" onClick={() => removeVariable(idx)}>x</button>
                  </div>
                </div>

                <div className="variable-row">
                  <div className="form-group">
                    <label className="form-label">Identificador *</label>
                    <input
                      type="text"
                      className="form-input mono"
                      value={v.key}
                      onChange={(e) => updateVariable(idx, { key: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })}
                      placeholder="dolar"
                    />
                    <div className="input-hint">Usar en formulas: solo letras, numeros y _</div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Etiqueta *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={v.label}
                      onChange={(e) => updateVariable(idx, { label: e.target.value })}
                      placeholder="Dolar"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Unidad</label>
                    <input
                      type="text"
                      className="form-input"
                      value={v.unit || ''}
                      onChange={(e) => updateVariable(idx, { unit: e.target.value })}
                      placeholder="$, %, cm"
                    />
                  </div>
                </div>

                {v.type === 'number' && (
                  <div className="form-group">
                    <label className="form-label">Valor por defecto</label>
                    <input
                      type="number"
                      className="form-input mono"
                      value={v.default_value}
                      onChange={(e) => updateVariable(idx, { default_value: e.target.value })}
                      step="0.01"
                    />
                  </div>
                )}

                {v.type === 'select' && (
                  <div className="form-group">
                    <label className="form-label">Opciones</label>
                    {(v.options || []).map((opt, oi) => (
                      <div key={oi} className="option-row">
                        <input
                          type="text"
                          className="form-input"
                          value={opt.label}
                          onChange={(e) => updateOption(idx, oi, { label: e.target.value })}
                          placeholder="Nombre"
                        />
                        <input
                          type="number"
                          className="form-input mono"
                          value={opt.value}
                          onChange={(e) => updateOption(idx, oi, { value: e.target.value })}
                          step="0.01"
                          placeholder="Valor"
                        />
                        <button className="btn-remove" onClick={() => removeOption(idx, oi)}>x</button>
                      </div>
                    ))}
                    <button className="btn-small" onClick={() => addOption(idx)}>+ Opcion</button>
                  </div>
                )}

                {v.type === 'checkbox' && (
                  <div className="variable-row">
                    <div className="form-group">
                      <label className="form-label">Valor si esta marcada</label>
                      <input
                        type="number"
                        className="form-input mono"
                        value={v.checked_value}
                        onChange={(e) => updateVariable(idx, { checked_value: e.target.value })}
                        step="0.01"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Valor si esta desmarcada</label>
                      <input
                        type="number"
                        className="form-input mono"
                        value={v.unchecked_value}
                        onChange={(e) => updateVariable(idx, { unchecked_value: e.target.value })}
                        step="0.01"
                      />
                    </div>
                  </div>
                )}

                {v.type === 'calculated' && (
                  <div className="form-group">
                    <label className="form-label">Formula de la variable</label>
                    <textarea
                      className="form-input mono formula-input"
                      value={v.formula || ''}
                      onChange={(e) => updateVariable(idx, { formula: e.target.value })}
                      placeholder="Ej: horahombre / 15"
                      rows={2}
                    />
                    <div className="input-hint">
                      Se calcula con otras variables globales y no se muestra para modificar en el presupuesto.
                    </div>
                  </div>
                )}
              </div>
            ))
          )}

          <div className="editor-actions" style={{ marginTop: '1rem' }}>
            <div className="editor-buttons">
              <button className="btn-primary-inline" onClick={handleSaveVariables} disabled={savingVariables}>
                {savingVariables ? 'Guardando...' : 'Guardar Variables'}
              </button>
            </div>
          </div>
            </div>
          ) : (
            <div className="settings-section-summary">
              {variables.length === 1 ? '1 variable global cargada' : `${variables.length} variables globales cargadas`}
            </div>
          )}
        </div>

        <ConfirmDialog
          open={!!deleteOperator}
          title="Eliminar operador"
          message={deleteOperator ? `¿Estas seguro de eliminar el operador "${deleteOperator.name}"?` : ''}
          confirmLabel="Eliminar"
          loading={savingOperator}
          onConfirm={handleDeleteOperator}
          onCancel={() => setDeleteOperator(null)}
        />
        <ConfirmDialog
          open={!!deleteWorker}
          title="Eliminar trabajador"
          message={deleteWorker ? `¿Estas seguro de eliminar el trabajador "${deleteWorker.name}"? Las ordenes asignadas quedaran sin trabajador.` : ''}
          confirmLabel="Eliminar"
          loading={savingWorker}
          onConfirm={handleDeleteWorker}
          onCancel={() => setDeleteWorker(null)}
        />
      </main>
    </div>
  );
};

export default Settings;
